import { useMemo, useState } from 'react';
import { SectionCard } from './SectionCard';
import { TemplateBuilder, type TemplateItemDraft } from './TemplateBuilder';
import { TemplateList } from './TemplateList';
import { Button } from '../ui/Button';
import { ALERTS, INPUTS } from '../../styles/classNames';
import type { Exercise, WorkoutTemplate } from '../../types';

interface AssignableClient {
  id: string; // linked /users uid, used by workoutAssignments.clientId and client-owned template filtering
  clientDocId?: string; // optional /clients doc id for display/debug only
  displayName?: string;
  phone?: string;
}

interface WorkoutTemplatesViewProps {
  // Data
  exercises: Exercise[];
  exerciseLookup: Map<string, Exercise>;
  templates: WorkoutTemplate[];
  clients: AssignableClient[];
  templateName: string;
  templateNotes: string;
  templateItems: TemplateItemDraft[];
  selectedTemplateId: string | null;
  error: string | null;

  // Actions
  onSetTemplateName: (name: string) => void;
  onSetTemplateNotes: (notes: string) => void;
  onSetTemplateItems: (setter: (items: TemplateItemDraft[]) => TemplateItemDraft[]) => void;
  onReorderTemplateItems: (oldIndex: number, newIndex: number) => void;
  onSaveTemplate: () => void;
  onStartTemplateEdit: (templateId: string) => void;
  onResetTemplateDraft: () => void;
}

export function WorkoutTemplatesView({
  exercises,
  exerciseLookup,
  templates,
  clients,
  templateName,
  templateNotes,
  templateItems,
  selectedTemplateId,
  error,
  onSetTemplateName,
  onSetTemplateNotes,
  onSetTemplateItems,
  onReorderTemplateItems,
  onSaveTemplate,
  onStartTemplateEdit,
  onResetTemplateDraft,
}: WorkoutTemplatesViewProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [clientFilter, setClientFilter] = useState<string>('library');

  // Determine if we're in edit mode (either creating new or editing existing)
  const showEditor = isEditing || selectedTemplateId !== null;

  // Filter templates by client
  const filteredTemplates = useMemo(() => {
    if (clientFilter === 'all') return templates;
    if (clientFilter === 'library') return templates.filter((t) => !t.clientId);
    return templates.filter((t) => t.clientId === clientFilter);
  }, [templates, clientFilter]);

  // Add item to template
  const handleAddItem = (item: TemplateItemDraft) => {
    onSetTemplateItems((prev) => [...prev, item]);
  };

  // Update item at index
  const handleUpdateItem = (index: number, item: TemplateItemDraft) => {
    onSetTemplateItems((prev) => {
      const next = [...prev];
      next[index] = item;
      return next;
    });
  };

  // Remove item at index
  const handleRemoveItem = (index: number) => {
    onSetTemplateItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  // Start creating a new template
  const handleAddTemplate = () => {
    onResetTemplateDraft();
    setIsEditing(true);
  };

  // Start editing an existing template
  const handleEditTemplate = (templateId: string) => {
    onStartTemplateEdit(templateId);
    setIsEditing(true);
  };

  // Cancel editing and go back to list
  const handleCancelEdit = () => {
    onResetTemplateDraft();
    setIsEditing(false);
  };

  // Save template and go back to list
  const handleSaveTemplate = () => {
    onSaveTemplate();
    setIsEditing(false);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
        {showEditor ? (
          /* Template Editor */
          <SectionCard
            eyebrow="Workout Builder"
            title={selectedTemplateId ? 'Edit Template' : 'New Template'}
            fillHeight
            action={
              <Button onClick={handleCancelEdit} variant="secondary" size="sm">
                Cancel
              </Button>
            }
          >
            {error && (
              <div className={`mb-4 ${ALERTS.error} shrink-0`}>
                {error}
              </div>
            )}
            <TemplateBuilder
              exercises={exercises}
              exerciseLookup={exerciseLookup}
              templateName={templateName}
              templateNotes={templateNotes}
              setTemplateName={onSetTemplateName}
              setTemplateNotes={onSetTemplateNotes}
              templateItems={templateItems}
              onAddItem={handleAddItem}
              onUpdateItem={handleUpdateItem}
              onReorderItems={onReorderTemplateItems}
              onRemoveItem={handleRemoveItem}
              onSave={handleSaveTemplate}
              mode={selectedTemplateId ? 'edit' : 'create'}
            />
          </SectionCard>
        ) : (
          /* Template List */
          <SectionCard
            eyebrow="Workout Management"
            title="Templates"
            fillHeight
            action={
              <Button onClick={handleAddTemplate} size="sm">
                + Add Template
              </Button>
            }
          >
            <div className="mb-3">
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                className={INPUTS.select}
              >
                <option value="library">Library Templates</option>
                <option value="all">All Templates</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName || c.phone || c.id}
                  </option>
                ))}
              </select>
            </div>
            <TemplateList
              templates={filteredTemplates}
              onEdit={handleEditTemplate}
            />
          </SectionCard>
        )}
    </div>
  );
}
